// @ts-strict-ignore
// noinspection JSNonASCIINames,NonAsciiCharacters

import {Component, computed, effect, inject, OnInit, signal} from "@angular/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {ExamSession_AssessmentForm, ExamSession_StationForm, ExamSessionStateService} from "../../service/exam-session-state.service";
import {Translations} from "@translations";
import {SimSessionService} from "@services";
import {
	AddStationToExamSessionRequest,
	Assessment,
	ExamAssessmentWrapper,
	ExamStation,
	FetchDataType,
	ParticipantType,
	SessionPhaseType,
	UpdateStationInExamSessionRequest,
} from "@models";
import {Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, filter} from "rxjs/operators";
import {FormRecord, ɵValue} from "@angular/forms";
import {SimChipAutocompleteComponent} from "../../../../../../../shared/shared-components/sim-advanced-inputs/autocomplete/sim-chip-autocomplete/sim-chip-autocomplete.component";
import {FetchData_TypeMapping} from "../../../../../../../shared/shared-components/sim-advanced-inputs/autocomplete/autocomplete.service";
import {EnumLabel} from "../../../../../../../shared/shared-pipes/enum-label.pipe";

@UntilDestroy()
@Component({
	selector: "sim-exam-session-step2",
	templateUrl: "./exam-session-step2.component.html",
	styleUrl: "exam-session-step2.component.scss",
	standalone: false,
})
export class ExamSessionStep2Component implements OnInit {
	// Signals
	public currentStationIndex = signal<number>(0);
	public displayStations = signal<ExamStation[]>([]); // maps station index to station id
	public loadedForms = signal<SimChipAutocompleteComponent<keyof FetchData_TypeMapping>[]>([]);
	public areFormsLoaded = computed<boolean>(() => this._computeAreFormsLoaded());
	public disableAddStation = computed<boolean>(() => this.displayStations()?.some((station) => !station.id));

	// Readonly
	protected readonly T = Translations;
	protected readonly FetchDataType = FetchDataType;
	protected readonly SessionPhaseType = SessionPhaseType;
	protected readonly EnumLabel = EnumLabel;

	// Injections
	private _stateService = inject(ExamSessionStateService);
	private _sessionService = inject(SimSessionService);

	// Service Signals
	public stationForm = computed<ExamSession_StationForm>(() => this._computeStationForm());
	public examPhases = this._stateService.examPhases;
	public excludeRoomIds = this._stateService.excludeRoomIds.asReadonly();
	public excludeUserIds = this._stateService.excludeUserIds.asReadonly();
	public excludeExaminerIds = this._stateService.excludeExaminerIds.asReadonly();

	// Properties
	public step2Form = this._stateService.step2Form;
	public isReady: boolean = true;
	private _subscription: Subscription | null = null;

	constructor() {
		effect(() => this._effectObserveFormChanges(), {allowSignalWrites: true});
		effect(() => this._effectValidateCurrentIndex(), {allowSignalWrites: true});
	}

	public ngOnInit() {
		this.displayStations.set(this._stateService.session.examStations);
		if (!this.displayStations().length) {
			this.addExamStation();
		}
	}

	public addExamStation(): void {
		this.displayStations.update((previousStations) =>
			previousStations.length ? [...previousStations, {} as ExamStation] : [{} as ExamStation],
		);
		this.step2Form.push(this._stateService.emptyStation);
		this.currentStationIndex.set(this.displayStations().length - 1);
	}

	public removeExamStation(stationId: string, index: number): void {
		// Pre-existing Station
		if (stationId) {
			this._sessionService
				.removeStationFromExamSession({stationId, sessionId: this._stateService.session.id})
				.then((session) => (this._stateService.session = session));
			this.displayStations.update((previousStations) => previousStations.filter((station) => station.id !== stationId));
		}
		// New Station (UI only)
		else {
			this.displayStations.update((previousStations) => previousStations.filter((station) => !!station.id));
		}
		// Form Control
		this.step2Form.removeAt(index);
		if (this.currentStationIndex() === this.displayStations().length) {
			this.currentStationIndex.set(this.displayStations().length - 1);
		}
	}

	protected onFormLoad(formField: SimChipAutocompleteComponent<keyof FetchData_TypeMapping>) {
		if (formField.formCtrl().value) {
			this.loadedForms.update((previousForms) => [...previousForms, formField]);
		}
	}

	private _effectObserveFormChanges() {
		// Validate
		if (!this.stationForm()) {
			return;
		}

		// Cooldown to prevent station cloning
		this.isReady = false;
		this.loadedForms.set([]);
		setTimeout(() => (this.isReady = true), 50);

		// Unsubscribe from previous
		if (this._subscription) {
			this._subscription.unsubscribe();
		}

		// Subscribe to changes
		this._subscription = this.stationForm()
			.valueChanges.pipe(
				untilDestroyed(this),
				filter(Boolean),
				filter(() => this.stationForm().valid),
				debounceTime(500),
			)
			.subscribe((value) => {
				this._upsertUserInput(value);
			});

		this.setDefaultScenarioAssessments();
	}

	private _effectValidateCurrentIndex() {
		const currentIndex = this.currentStationIndex();
		if (currentIndex === null || !this.stationForm() || currentIndex >= this.displayStations().length) {
			this.currentStationIndex.set(this.displayStations().length - 1);
		}
	}

	private _upsertUserInput(value: ɵValue<ExamSession_StationForm>) {
		const request: AddStationToExamSessionRequest = {
			sessionId: this._stateService.session.id,
			examinerUserId: value.examiner?.id,
			scenarioId: value.scenario.id,
			trainingRoomId: value.trainingRoom.id,
			standardizedPatientUserId: value.standardizedPatient?.id,
			assessmentWrappers: this._buildAssessmentWrappers(value.assessments),
		};
		const stationId = this._stateService.session.examStations.at(this.currentStationIndex())?.id;
		const savePromise = stationId
			? this._sessionService.updateStationInExamSession({stationId, ...request} as UpdateStationInExamSessionRequest)
			: this._sessionService.addStationToExamSession(request);
		this._stateService.savePromise = savePromise;
		savePromise.then((session) => {
			this._stateService.session = session;
			this.displayStations.set(session.examStations);
		});
	}

	private _buildAssessmentWrappers(assessments: ɵValue<FormRecord<ExamSession_AssessmentForm>>): ExamAssessmentWrapper[] {
		const result = [];
		this._stateService.examPhases().forEach((phase) => {
			const assessment = assessments[SessionPhaseType[phase]];
			if (!assessment) {
				return;
			}

			// Examiner
			if (assessment.examiner?.id) {
				result.push({
					assessmentId: assessment.examiner.id,
					sessionPhaseType: phase,
					participantType: ParticipantType.Instructor,
				} as ExamAssessmentWrapper);
			}

			// Standardized Patient
			if (assessment.standardizedPatient?.id) {
				result.push({
					assessmentId: assessment.standardizedPatient.id,
					sessionPhaseType: phase,
					participantType: ParticipantType.StandardizedPatient,
				} as ExamAssessmentWrapper);
			}

			// Student
			if (assessment.student?.id) {
				result.push({
					assessmentId: assessment.student.id,
					sessionPhaseType: phase,
					participantType: ParticipantType.Learner,
				} as ExamAssessmentWrapper);
			}
		});
		return result;
	}

	private _computeAreFormsLoaded(): boolean {
		if (!this.stationForm()) {
			return true;
		}
		let result = true;
		if (this.stationForm().value.examiner?.id) {
			result = result && this.loadedForms().some((form) => form.selectedOption?.id === this.stationForm().value.examiner.id);
		}
		if (this.stationForm().value.standardizedPatient?.id) {
			result =
				result && this.loadedForms().some((form) => form.selectedOption?.id === this.stationForm().value.standardizedPatient.id);
		}
		if (this.stationForm().value.scenario?.id) {
			result = result && this.loadedForms().some((form) => form.selectedOption?.id === this.stationForm().value.scenario.id);
		}
		if (this.stationForm().value.trainingRoom?.id) {
			result = result && this.loadedForms().some((form) => form.selectedOption?.id === this.stationForm().value.trainingRoom.id);
		}
		return result;
	}

	private _computeStationForm(): ExamSession_StationForm {
		const result = this._stateService.step2Form.at(this.currentStationIndex());
		result?.markAsPristine();
		return result;
	}

	private setDefaultScenarioAssessments(): void {
		// Local function
		const setDefaultAssessments = (defaultAssessments: ExamAssessmentWrapper[] | null) => {
			// 1. Clear values if no default assessments
			if (!defaultAssessments?.length) {
				this.stationForm().controls.assessments.reset();
				return;
			}

			// 2. Create a temporary helper map to store assessments for each active phase
			const helperMap = new Map<SessionPhaseType, {participantType: ParticipantType; assessmentId: string}[]>(
				this.examPhases().map(
					(phase) =>
						[phase, []] as [
							SessionPhaseType,
							{
								participantType: ParticipantType;
								assessmentId: string;
							}[],
						],
				),
			);

			// 3. Filter out assessments from inactive phases
			const relevantAssessments = defaultAssessments.filter((defaultAssessment) =>
				this.examPhases().includes(defaultAssessment.sessionPhaseType),
			);

			// 4. Populate map
			relevantAssessments.forEach((assessment) => {
				helperMap.set(assessment.sessionPhaseType, [
					...helperMap.get(assessment.sessionPhaseType),
					{participantType: assessment.participantType, assessmentId: assessment.assessmentId},
				]);
			});

			// 5. Use helper map to set form values
			helperMap.forEach((participantAssessments, phaseType) => {
				const relevantAssessmentForm = this.stationForm().controls.assessments.get(SessionPhaseType[phaseType]);
				const examinerAssessmentId = participantAssessments.find(
					(value) => value.participantType === ParticipantType.Instructor,
				)?.assessmentId;
				const standardizedPatientAssessmentId = participantAssessments.find(
					(value) => value.participantType === ParticipantType.StandardizedPatient,
				)?.assessmentId;
				const studentAssessmentId = participantAssessments.find(
					(value) => value.participantType === ParticipantType.Learner,
				)?.assessmentId;

				relevantAssessmentForm.setValue({
					examiner: examinerAssessmentId ? ({id: examinerAssessmentId} as Assessment) : null,
					standardizedPatient: standardizedPatientAssessmentId ? ({id: standardizedPatientAssessmentId} as Assessment) : null,
					student: studentAssessmentId ? ({id: studentAssessmentId} as Assessment) : null,
				});
				this.stationForm().controls.assessments.updateValueAndValidity();
			});
		};

		// Subscription
		this.stationForm()
			.controls.scenario.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged(), filter(Boolean), debounceTime(500))
			.subscribe((scenario) => setDefaultAssessments(scenario.defaultAssessments));
	}
}
